import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import styles from "./IntegrationList.module.scss";

// Icon imports
import SettingsIcon from "../../assets/icons/SettingsIcon";
import TelegramIcon from "../../assets/icons/TelegramIcon";
import UsersIcon from "../../assets/icons/UsersIcon";
import { WidgetIcon } from "../../assets/icons/WidgetIcon";
import { MessageBotIcon } from "../../assets/icons/MessageBotIcon";
import WhatsAppIcon from "../../assets/icons/WhatsAppIcon";
import AvitoIcon from "../../assets/icons/AvitoIcon";
import { LogoVkIcon } from "../../assets/icons/LogoVkIcon";
import { DialogFormIcon } from "../../assets/icons/DialogFormIcon";
import { BitrixIcon } from "../../assets/icons/BitrixIcon";
import InstagramIcon from "../../assets/icons/InstagramIcon";
import RentProgIcon from "../../assets/icons/RentProgIcon";
import { GoogleSheetIcon } from "../../assets/icons/GoogleSheetIcon";
import { AmoCRMIcon } from "../../assets/icons/AmoCRMIcon";

// Action imports
import { setActiveBot } from "../../store/slices/integrationSlice";
import { setActiveWidget } from "../../store/slices/widgetSlice";
import { setActiveAssistantWF } from "../../store/slices/asistantWFSlice";
import { setActiveDataRentProg } from "../../store/slices/rentProgSlice";
import { setActiveDataGoogleSheets } from "../../store/slices/googleSheetsSlice";
// import { setActiveDataAmo } from "../../store/slices/amoSlice";

// API actions
import { getAssistantList } from "../../utils/api/actions/assistant";
import { getWidgetList } from "../../utils/api/actions/widget";
import { getIntegrationList } from "../../utils/api/actions/integrations/tgBot";
import {
  getTelegramIntegration,
  getTelegramIntegrations,
} from "../../utils/api/actions/integrations/telegram";
import {
  getWhatsappIntegration,
  getWhatsappIntegrations,
} from "../../utils/api/actions/integrations/whatsapp";
import {
  getAvitoIntegration,
  getAvitoIntegrations,
} from "../../utils/api/actions/integrations/avito";
import {
  getVkIntegration,
  getVkIntegrations,
} from "../../utils/api/actions/integrations/vk";
import { getAssistantWFList } from "../../utils/api/actions/integrations/assistantForm";
import {
  getIntegrationBitrix,
  getIntegrationsBitrixList,
} from "../../utils/api/actions/integrations/bitrix";
import {
  getInstagramIntegration,
  getInstagramIntegrations,
} from "../../utils/api/actions/integrations/instagram";
import {
  getIntegrationBitrixBot,
  getIntegrationsBitrixBotList,
} from "../../utils/api/actions/integrations/bitrixBot";
import { getGoogleSheetData } from "../../utils/api/actions/integrations/googleSheets";
import {
  getIntegrationAmo,
  getIntegrationsAmoList,
} from "../../utils/api/actions/integrations/amo";

// Components
import Chip from "../../ui/Chip/Chip";
import { getIntegrationsRentProg } from "../../utils/api/actions/integrations/rentProg";

export default function IntegrationList() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const id =
    useSelector((state) => state.user?.data?.id) ||
    localStorage.getItem("@storage_User_Id");

  // Get data from Redux store
  const dataWidgets = useSelector((state) => state?.widget?.dataWidgets);
  const { dataTg, dataInst, dataWa, dataBots, dataBitrix } = useSelector(
    (state) => state.integration
  );
  const dataAvito = useSelector((state) => state?.avito?.dataAvito);
  const dataVk = useSelector((state) => state?.vk?.dataVk);
  const dataAssistantWF = useSelector(
    (state) => state?.assistantWF?.dataAssistantWF
  );
  const dataBitrixChatBot = useSelector(
    (state) => state?.bitrixChatBot?.dataBitrixChatBot
  );
  const dataGoogleSheets = useSelector(
    (state) => state?.googleSheets?.dataGooglSheets
  );
  const { dataRentProg } = useSelector((state) => state?.rentProg);
  const { dataAmo } = useSelector((state) => state?.amo);

  React.useEffect(() => {
    dispatch(getIntegrationList(id));
    dispatch(getAssistantList(id));
    dispatch(getWidgetList(id));
    dispatch(getAssistantWFList(id));
    dispatch(getTelegramIntegrations(id));
    dispatch(getWhatsappIntegrations(id));
    dispatch(getInstagramIntegrations(id));
    dispatch(getAvitoIntegrations(id));
    dispatch(getVkIntegrations(id));
    dispatch(getIntegrationsBitrixList(id));
    dispatch(getIntegrationsBitrixBotList(id));
    dispatch(getIntegrationsAmoList(id));
    dispatch(getGoogleSheetData(id));
    dispatch(getIntegrationsRentProg(id));
  }, [dispatch, id]);

  // Combine all integrations into a single array
  const combinedData = [
    ...(dataBots || []).map((bot) => ({ ...bot, type: "bot" })),
    ...(dataTg || []).map((tg) => ({ ...tg, type: "telegram" })),
    ...(dataWa || []).map((wa) => ({ ...wa, type: "whatsapp" })),
    ...(dataInst || []).map((inst) => ({ ...inst, type: "instagram" })),
    ...(dataVk || []).map((vk) => ({ ...vk, type: "vk" })),
    ...(dataAvito || []).map((avito) => ({ ...avito, type: "avito" })),
    ...(dataBitrix || []).map((bitrix) => ({ ...bitrix, type: "bitrix" })),
    ...(dataBitrixChatBot || []).map((bitrixBot) => ({
      ...bitrixBot,
      type: "bitrixBot",
      active: true,
    })),
    ...(dataAmo || []).map((amo) => ({ ...amo, type: "amo" })),
    ...(dataGoogleSheets || []).map((googleSheet) => ({
      ...googleSheet,
      type: "googleSheet",
      active: true,
    })),
    ...(dataWidgets || []).map((widget) => ({ ...widget, type: "widget" })),
    ...(dataAssistantWF || []).map((assist) => ({
      ...assist,
      type: "assistantWF",
    })),
    ...(dataRentProg || []).map((rent) => ({
      ...rent,
      type: "rentProg",
    })),
  ];

  // Handle click events for different integration types
  const handleNavigation = (el, type) => {
    switch (type) {
      case "bot":
        navigate(`telegram-bot/active/${el.id}`);
        dispatch(setActiveBot({ activeBot: el, i: combinedData.indexOf(el) }));
        localStorage.setItem(
          "activeBot",
          JSON.stringify({ activeBot: el, i: combinedData.indexOf(el) })
        );
        break;
      case "telegram":
        navigate(`telegram/${el.externalTelegramId}`);
        dispatch(getTelegramIntegration(el.userId, el.externalTelegramId));
        break;
      case "whatsapp":
        navigate(`whatsapp/${el.internalWhatsappId}`);
        dispatch(getWhatsappIntegration(el.userId, el.externalWhatsappId));
        break;
      case "instagram":
        navigate(`instagram/${el.externalInstagramId}`);
        dispatch(getInstagramIntegration(el.userId, el.externalInstagramId));
        break;
      case "avito":
        navigate(`avito/${el.externalAvitoId}`);
        dispatch(getAvitoIntegration(el.userId, el.externalAvitoId));
        break;
      case "vk":
        navigate(`vk/${el.groupId}`);
        dispatch(getVkIntegration(el.userId, el.groupId));
        break;
      case "assistantWF":
        navigate(`dialog-form/active/${el.id}`);
        dispatch(setActiveAssistantWF(el));
        localStorage.setItem("activeAssistantWF", JSON.stringify(el));
        break;
      case "bitrix":
        navigate(`bitrix/${el.id}`);
        dispatch(getIntegrationBitrix(el.userId, el.clientId));
        break;
      case "amo":
        navigate(`amo/${el.integrationId}`);
        dispatch(getIntegrationAmo(id, el.integrationId));
        break;
      case "bitrixBot":
        navigate(`bitrix-chat-bot/${el.id}`);
        dispatch(getIntegrationBitrixBot(id, el.bitrixBotId));
        break;
      case "googleSheet":
        navigate(`google-sheet/${el.id}`);
        dispatch(setActiveDataGoogleSheets(el));
        break;
      case "rentProg":
        navigate(`rent-prog/${el.id}`);
        dispatch(setActiveDataRentProg(dataRentProg[0]));
        localStorage.setItem("activeRentProg", JSON.stringify(dataRentProg[0]));
        break;
      // case "amo":
      //   navigate(`amo/${el.id}`);
      //   dispatch(setActiveDataAmo(dataAmo));
      //   break;
      default: // widget
        navigate(`widget/active/${el.id}`);
        dispatch(setActiveWidget(el));
        localStorage.setItem("activeWidget", JSON.stringify(el));
    }
  };

  // Get appropriate icon component based on integration type
  const getIntegrationIcon = (type) => {
    switch (type) {
      case "bot":
        return (
          <MessageBotIcon sizeContainer={"30px"} size={20} fill={"white"} />
        );
      case "telegram":
        return <TelegramIcon width={20} height={20} fill={"#3e8fcb"} />;
      case "whatsapp":
        return <WhatsAppIcon size={20} fill={"#43d854"} />;
      case "instagram":
        return <InstagramIcon size={20} />;
      case "avito":
        return <AvitoIcon size={20} />;
      case "vk":
        return <LogoVkIcon fillF={"#0077FF"} fillS={"white"} />;
      case "assistantWF":
        return <DialogFormIcon size={20} fill={"#3e8fcb"} />;
      case "bitrix":
      case "bitrixBot":
        return <BitrixIcon width={35} height={20} fill={"#3e8fcb"} />;
      case "googleSheet":
        return <GoogleSheetIcon size={20} />;
      case "rentProg":
        return <RentProgIcon />;
      case "amo":
        return <AmoCRMIcon />;
      default: // widget
        return <WidgetIcon width={20} height={20} fill={"#3e8fcb"} />;
    }
  };

  // Get display name based on integration type and data
  const getDisplayName = (item) => {
    const { type } = item;

    if (type === "telegram" || type === "whatsapp") {
      return item.phoneNumber;
    } else if (type === "amo") {
      return "amo";
    } else if (type === "googleSheet") {
      return "Spread Sheet";
    } else if (type === "instagram") {
      return item?.username;
    } else if (type === "rentProg") {
      return "RentProg";
    } else {
      return `${item.name} ${item.lastName || ""}`;
    }
  };

  // Get description based on integration type
  const getDescription = (item) => {
    const { type } = item;

    if (item.about) return item.about;

    switch (type) {
      case "bot":
        return "Telegram бот";
      case "telegram":
        return "Аккаунт Telegram";
      case "whatsapp":
        return "Аккаунт WhatsApp";
      case "instagram":
        return "Аккаунт Instagram";
      case "avito":
        return "Аккаунт Avito";
      case "vk":
        return "Группа ВКонтакте";
      case "bitrix":
        return "Bitrix24";
      case "rentProg":
        return "RentProg";
      case "bitrixBot":
        return "Bitrix24 чат бот";
      case "googleSheet":
        return "Google Sheets - документ";
      case "amo":
        return "AmoCRM";
      case "assistantWF":
        return `Форма на сайте: ${item.allowedDomains}`;
      default: // widget
        return `Виджет на сайте: ${item.allowedDomains}`;
    }
  };

  return (
    <div className={styles.container}>
      <table className={styles.table}>
        <thead>
          <tr className={styles.headerRow}>
            <th className={styles.headerCell}></th>
            <th className={styles.headerCell}>Название</th>
            <th className={styles.headerCell}>Статус</th>
            <th className={styles.headerCell}>Описание</th>
            <th className={styles.headerCell}>
              <UsersIcon />
            </th>
            <th className={styles.headerCell}></th>
          </tr>
        </thead>
        <tbody className={styles.tableBody}>
          {combinedData.map((item, index) => (
            <tr
              className={styles.tableRow}
              key={index}
              onClick={() => handleNavigation(item, item.type)}
            >
              <td className={styles.iconCell}>
                {getIntegrationIcon(item.type)}
              </td>
              <td className={styles.nameCell}>{getDisplayName(item)}</td>
              <td className={styles.statusCell}>
                {item.active && (
                  <Chip
                    active={item.active}
                    title={item.active ? "Активен" : "Не активен"}
                  />
                )}
              </td>
              <td className={styles.descriptionCell}>{getDescription(item)}</td>
              <td className={styles.usersCell}>
                {item.userCount ?? item.chatsCount ?? item.chats ?? ""}
              </td>
              <td className={styles.settingsCell}>
                <SettingsIcon />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
